import {FormInstance} from 'antd/es/form';
import {useEffect, useState} from 'react';

export interface IWatcherCallbackArg {
    value: any;
    formValues: ReturnType<FormInstance['getFieldsValue']>;
    formInstance: FormInstance;
}

export interface IFieldWatchersApplicationResult {
    [key: string]: { [key: string]: boolean };
}

export enum EWatcherFieldFlag {
    isHidden = 'isHidden',
    isDisabled = 'isDisabled',
    isRequired = 'isRequired',
    isLoading = 'isLoading',
    shouldFilterLookup = 'shouldFilterLookup',
}

export interface IWatcher {
    isNotInput?: boolean;
    condition: (arg: IWatcherCallbackArg) => boolean;
    perform?: (arg: IWatcherCallbackArg) => void;
    flag?: EWatcherFieldFlag;
}

export interface IUseFormFieldsManager {
    formInstance: FormInstance;
    watchers: { [fieldKey: string]: IWatcher[] };
    triggerDeps?: any[];
}

export type TFieldWatcher = IUseFormFieldsManager['watchers'][string][0];

export const useFormFieldsManager = ({
    formInstance,
    watchers,
    triggerDeps = [],
}: IUseFormFieldsManager) => {
    const [watchersApplicationResult, setWatchersApplicationResult] = useState<IFieldWatchersApplicationResult>(
        Object.keys(watchers).reduce((prev, cur) => {
            prev[cur] = {};
            return prev;
        }, {} as IFieldWatchersApplicationResult),
    );

    const applyFieldWatchers = () => {
        const formValues = formInstance.getFieldsValue();
        const applicationResult: IFieldWatchersApplicationResult = {};
        Object.entries(watchers).forEach(([fieldKey, watchersList]) => {
            const applyWatcher = (watcher: TFieldWatcher) => {
                const {
                    condition, flag, perform, isNotInput,
                } = watcher;
                const watcherCallbackArg = {
                    value: isNotInput ? undefined : formInstance.getFieldValue(fieldKey),
                    formValues,
                    formInstance,
                };

                const isTriggered = condition(watcherCallbackArg);

                (() => { // put application result
                    if (!flag) return;
                    if (!applicationResult[fieldKey]) applicationResult[fieldKey] = {};
                    applicationResult[fieldKey][flag] = isTriggered;
                })();

                if (isTriggered && perform) perform(watcherCallbackArg);
            };
            watchersList.forEach(watcher => {
                applyWatcher(watcher);
            });
        });
        setWatchersApplicationResult(applicationResult);
        return applicationResult;
    };

    useEffect(() => {
        if (triggerDeps.length) applyFieldWatchers();
    }, [...triggerDeps]);

    const checkWatcherFlag = (fieldKey: string, flag: EWatcherFieldFlag) => {
        if (!watchersApplicationResult[fieldKey]) {
            throw new Error(`Нет обработчика для поля ${fieldKey} в useFormFieldsManager.`);
        }
        return watchersApplicationResult[fieldKey][flag] === true;
    };

    return {
        watchersApplicationResult,
        applyFieldWatchers,
        checkWatcherFlag,
    };
};

interface ICreateDisableFieldOnValueWatcherArgs {
    value: any;
    setTo: any;
}

export const createDisableFieldOnValueWatcher = (
    {
        value,
        setTo,
    }: ICreateDisableFieldOnValueWatcherArgs,
) => ({
    condition: () => value === setTo,
    flag: EWatcherFieldFlag.isDisabled,
});
